import { create } from 'zustand';
import { chargebacksApi } from '../api/chargebacks';

export const useChargebackStore = create((set, get) => ({
  // Step 1 - OEM & Period
  currentStep: 1,
  selectedOem: null,
  periodStart: null,
  periodEnd: null,
  selectedProgram: null,

  // Step 2 - Part Selection
  selectedPartIds: [],
  eligibleParts: [],
  loadingParts: false,

  // Step 3 - Review & Generate
  chargebackId: null,
  pdfUrl: null,
  generating: false,

  // Actions
  setStep: (step) => set({ currentStep: step }),

  setOem: (oem) => set({ selectedOem: oem }),

  setPeriod: (start, end) => set({ periodStart: start, periodEnd: end }),

  setProgram: (program) => set({ selectedProgram: program }),

  loadEligibleParts: async () => {
    const { selectedOem, periodStart, periodEnd, selectedProgram } = get();
    
    set({ loadingParts: true });
    try {
      const response = await chargebacksApi.getEligibleParts({
        oem: selectedOem,
        period_start: periodStart,
        period_end: periodEnd,
        program: selectedProgram
      });
      
      if (response.success) {
        set({ eligibleParts: response.data, loadingParts: false });
      }
    } catch (error) {
      console.error('Failed to load eligible parts:', error);
      set({ loadingParts: false });
    }
  },

  togglePart: (partId) => {
    const { selectedPartIds } = get();
    const isSelected = selectedPartIds.includes(partId);
    
    set({
      selectedPartIds: isSelected
        ? selectedPartIds.filter(id => id !== partId)
        : [...selectedPartIds, partId]
    });
  },

  selectAllParts: () => {
    const { eligibleParts } = get();
    set({ selectedPartIds: eligibleParts.map(part => part.id) });
  },

  deselectAllParts: () => set({ selectedPartIds: [] }),

  createChargeback: async () => {
    const { selectedOem, periodStart, periodEnd, selectedPartIds } = get();
    
    try {
      const response = await chargebacksApi.createChargeback({
        oem: selectedOem,
        period_start: periodStart,
        period_end: periodEnd,
        part_ids: selectedPartIds
      });
      
      if (response.success) {
        set({ chargebackId: response.data.chargeback_id });
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to create chargeback');
      }
    } catch (error) {
      console.error('Failed to create chargeback:', error);
      throw new Error(error.message || 'Failed to create chargeback');
    }
  },

  generatePdf: async () => {
    const { chargebackId } = get();
    
    if (!chargebackId) {
      throw new Error('No chargeback ID available. Please create chargeback first.');
    }
    
    set({ generating: true });
    try {
      const response = await chargebacksApi.generatePdf(chargebackId);
      
      if (response.success && response.data) {
        set({ pdfUrl: response.data.pdf_url, generating: false });
        return response.data;
      } else {
        set({ generating: false });
        throw new Error(response.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      set({ generating: false });
      throw new Error(error.message || 'Failed to generate PDF');
    }
  },

  reset: () => set({
    currentStep: 1,
    selectedOem: null,
    periodStart: null,
    periodEnd: null,
    selectedProgram: null,
    selectedPartIds: [],
    eligibleParts: [],
    chargebackId: null,
    pdfUrl: null,
    generating: false
  }),

  // Computed values
  getTotalAmount: () => {
    const { selectedPartIds, eligibleParts } = get();
    return eligibleParts
      .filter(part => selectedPartIds.includes(part.id))
      .reduce((sum, part) => sum + part.tariff_cents, 0);
  },

  getTotalValue: () => {
    const { selectedPartIds, eligibleParts } = get();
    return eligibleParts
      .filter(part => selectedPartIds.includes(part.id))
      .reduce((sum, part) => sum + part.value_cents, 0);
  },

  isStep1Valid: () => {
    const { selectedOem, periodStart, periodEnd } = get();
    return !!(selectedOem && periodStart && periodEnd);
  },

  isStep2Valid: () => {
    const { selectedPartIds } = get();
    return selectedPartIds.length > 0;
  }
}));
